import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Flex, Text, Icon, Input, Skeleton, ScrollRail } from '@nula/core-ui';
import { TranscriptPlayer } from './transcript-player';
import { buildTurnAnchors } from './build-turn-anchors';
import { activeAnchorOnScroll } from './active-anchor-on-scroll';
import { TranscriptTurn } from './transcript-turn';
import { SpeakerChips } from './speaker-chips';
import { activeSegmentIndex } from './active-segment';
import type { PlaybackUrlFetcher } from './use-audio-playback';
import { dispatchTranscriptSeek, TRANSCRIPT_PLAYHEAD_EVENT, type TranscriptPlayheadEventDetail } from './transcript-events';
import type { TranscriptSegmentData } from './parse-transcript';
import type { SpeakerLabel } from './types/types';
import styles from './transcript-reader.module.css';

export interface TranscriptViewProps {
  recordingId: string;
  /** The transcript turns to render (already parsed). */
  segments: TranscriptSegmentData[];
  /** True while the transcript is still being fetched (shows skeletons). */
  loading: boolean;
  /** Speaker-id -> label overrides (drive avatar/chip/inline name). */
  speakerLabels: Record<string, SpeakerLabel>;
  /** Linked calendar event id (attendee suggestions in the label editor). */
  calendarEventId: string | null;
  /** The entity the attendees live on (the recording's notes entity). */
  entityId: string | null;
  /** This recording's audio surface. Scopes the attendee roster; omitted
   * reproduces the document-level roster. */
  surfaceId?: string | null;
  /** email -> display name for labeled speakers. */
  attendeeNameByEmail: Record<string, string>;
  /** email -> avatar URL for labeled speakers. */
  attendeePhotoByEmail: Record<string, string>;
  /** True while attendees are loading (turn editor shows a skeleton). */
  attendeesLoading: boolean;
  /** Share path: fetch the audio playback URL token-scoped instead of via the
   * authenticated recordings route. Forwarded to the player. */
  fetchPlaybackUrl?: PlaybackUrlFetcher;
  className?: string;
}

/**
 * Presentational transcript reader: search + player toolbar, per-speaker chips,
 * and the avatar-lead turn stream with playhead-driven active highlight + a
 * scroll-spy rail. Pure function of its props — NO store or HTTP reads — so it
 * renders identically in the authenticated app (TranscriptReader wraps it with
 * live data) and on the public share path (SurfaceShareTranscript wraps it with
 * server-fetched data and empty attendee maps).
 */
export function TranscriptView({
  recordingId,
  segments,
  loading,
  speakerLabels,
  calendarEventId,
  entityId,
  surfaceId,
  attendeeNameByEmail,
  attendeePhotoByEmail,
  attendeesLoading,
  fetchPlaybackUrl,
  className,
}: TranscriptViewProps) {
  const [search, setSearch] = useState('');
  const [playheadMs, setPlayheadMs] = useState<number | null>(null);

  // Subscribe to the audio player's playhead to track the active segment.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<TranscriptPlayheadEventDetail>).detail;
      if (detail.recordingId !== recordingId) return;
      setPlayheadMs(detail.currentTimeMs);
    };
    window.addEventListener(TRANSCRIPT_PLAYHEAD_EVENT, handler);
    return () => window.removeEventListener(TRANSCRIPT_PLAYHEAD_EVENT, handler);
  }, [recordingId]);

  const activeIndex = useMemo(() => activeSegmentIndex(segments, playheadMs), [segments, playheadMs]);

  // Auto-scroll the active turn into view (skipped under reduced-motion).
  const streamRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (activeIndex < 0) return;
    const el = streamRef.current?.querySelector<HTMLElement>(`[data-turn-index="${activeIndex}"]`);
    if (!el) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
  }, [activeIndex]);

  const needle = search.trim().toLowerCase();
  const visible = useMemo(
    () =>
      segments
        .map((seg, i) => ({ seg, i }))
        .filter(({ seg }) => !needle || seg.text.toLowerCase().includes(needle)),
    [segments, needle],
  );

  const handleSeek = (startMs: number) => dispatchTranscriptSeek({ startMs, recordingId });
  const boundariesMs = useMemo(() => segments.map((s) => s.start_ms), [segments]);

  // ScrollRail anchors — one per turn (labelled with the resolved speaker name).
  const railItems = useMemo(
    () => buildTurnAnchors(segments, speakerLabels, attendeeNameByEmail),
    [segments, speakerLabels, attendeeNameByEmail],
  );
  const anchorTurnIndices = useMemo(
    () => new Set(railItems.map((a) => a.turnIndex)),
    [railItems],
  );

  // The active rail anchor follows the SCROLL position (scroll-spy).
  const [activeAnchorId, setActiveAnchorId] = useState<string | null>(null);
  useEffect(() => {
    const scrollEl = streamRef.current;
    if (!scrollEl) return;
    const recompute = () => {
      const els = Array.from(scrollEl.querySelectorAll<HTMLElement>('[data-anchor-id]'));
      const containerTop = scrollEl.getBoundingClientRect().top;
      setActiveAnchorId(activeAnchorOnScroll(els, containerTop, 40));
    };
    recompute();
    scrollEl.addEventListener('scroll', recompute, { passive: true });
    return () => scrollEl.removeEventListener('scroll', recompute);
  }, [railItems]);

  const jumpToAnchor = useCallback((id: string) => {
    const el = streamRef.current?.querySelector<HTMLElement>(`[data-anchor-id="${id}"]`);
    if (!el) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  }, []);

  return (
    <Flex vertical className={className ? `${styles.root} ${className}` : styles.root}>
      {/* Top bar: search (2/3) + squircle player (1/3). No Play-all. */}
      <Flex align="center" gap="sm" className={styles.toolbar}>
        <Input
          label="Search transcript"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          prefix={<Icon name="search" size={14} />}
          allowClear
          className={styles.search}
        />
        <Flex align="center" className={styles.playerSlot}>
          <TranscriptPlayer recordingId={recordingId} boundariesMs={boundariesMs} fetchPlaybackUrl={fetchPlaybackUrl} />
        </Flex>
      </Flex>

      <SpeakerChips
        segments={segments}
        speakerLabels={speakerLabels}
        attendeeNameByEmail={attendeeNameByEmail}
        entityId={entityId}
        surfaceId={surfaceId}
      />

      <Flex vertical className={styles.scrollWrapper}>
        {railItems.length > 0 && (
          <ScrollRail
            className={styles.scrollRail}
            items={railItems}
            activeId={activeAnchorId}
            onJump={jumpToAnchor}
            onScrollToTop={() => streamRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
          />
        )}
        <Flex vertical ref={streamRef} className={styles.stream} data-scroll-container="transcript">
          <Flex vertical className={styles.streamContent}>
            {loading && (
              <Flex vertical gap="md" data-testid="transcript-loading" className={styles.loading}>
                {[0, 1, 2, 3].map((i) => (
                  <Skeleton key={i} active title={{ width: '30%' }} paragraph={{ rows: 2, width: ['100%', '75%'] }} />
                ))}
              </Flex>
            )}

            {!loading && segments.length === 0 && (
              <Flex vertical align="center" justify="center" gap="sm" className={styles.empty}>
                <Icon name="record_voice_over" size={32} />
                <Text type="secondary" size="sm">No transcript available for this recording.</Text>
              </Flex>
            )}

            {!loading && segments.length > 0 && (
              <>
                {visible.length === 0 && (
                  <Text type="secondary" size="sm" className={styles.noMatch}>
                    No segments match “{search}”.
                  </Text>
                )}
                {renderTurns(visible, activeIndex, handleSeek, recordingId, calendarEventId, entityId, surfaceId, speakerLabels, attendeeNameByEmail, attendeePhotoByEmail, attendeesLoading, anchorTurnIndices)}
              </>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
}

/**
 * Render the visible turns, computing `showAvatar` from the previous *visible*
 * turn's speaker (so a filtered list still merges consecutive same-speaker
 * turns correctly), and tagging each with its original index for auto-scroll.
 */
function renderTurns(
  visible: { seg: TranscriptSegmentData; i: number }[],
  activeIndex: number,
  onSeek: (ms: number) => void,
  recordingId: string,
  calendarEventId: string | null,
  entityId: string | null,
  surfaceId: string | null | undefined,
  speakerLabels: Record<string, SpeakerLabel>,
  attendeeNameByEmail: Record<string, string>,
  attendeePhotoByEmail: Record<string, string>,
  attendeesLoading: boolean,
  anchorTurnIndices: Set<number>,
) {
  return visible.map(({ seg, i }, pos) => {
    const prev = pos > 0 ? visible[pos - 1].seg : null;
    const showAvatar = !prev || (prev.speaker ?? 'Unknown') !== (seg.speaker ?? 'Unknown');
    return (
      <div
        key={`${seg.start_ms}-${i}`}
        data-turn-index={i}
        data-anchor-id={anchorTurnIndices.has(i) ? `turn-${i}` : undefined}
      >
        <TranscriptTurn
          segment={seg}
          showAvatar={showAvatar}
          isPlaying={i === activeIndex}
          onSeek={onSeek}
          recordingId={recordingId}
          calendarEventId={calendarEventId}
          entityId={entityId}
          surfaceId={surfaceId}
          speakerLabels={speakerLabels}
          attendeeNameByEmail={attendeeNameByEmail}
          attendeePhotoByEmail={attendeePhotoByEmail}
          attendeesLoading={attendeesLoading}
        />
      </div>
    );
  });
}
